export type FfmpegParamsValidationResult = {
    valid: boolean;
    /** Parsed args with quotes stripped — safe to splice into a spawn() argv. */
    args: string[];
    error?: string;
};

const MAX_LENGTH = 512;
const MAX_ARGS = 64;

// Output-encoding flags users may set. Anything not matching is rejected, which
// implicitly blocks inputs (-i), formats (-f), mappings, filter scripts, log
// files and extra outputs.
const ALLOWED_FLAG_PATTERN =
    /^-(c|vcodec|acodec|crf|qp|b|minrate|maxrate|bufsize|preset|tune|profile|level|g|bf|refs|r|pix_fmt|vf|color_range|color_primaries|color_trc|colorspace|movflags|tag|x264-params|x265-params)(:(v|a|v:0|a:0))?$/;

const VALUELESS_FLAGS = new Set(["-an"]);

// Rejected anyway by the allowlist; listed here only to give clearer errors.
const DENIED_FLAGS = new Set(["-i", "-f", "-map", "-filter_complex", "-lavfi", "-filter_script", "-passlogfile", "-y"]);

// No slashes, backslashes, tildes or shell metacharacters — blocks paths and
// URLs structurally.
const VALUE_PATTERN = /^[A-Za-z0-9_+.,:=-]+$/;

const FORBIDDEN_VALUE_SUBSTRINGS = ["://", "movie", "file:", "pipe:", "concat", "subfile", "data:"];

// Filters that cannot touch files, network or other inputs.
const ALLOWED_VF_FILTERS = new Set([
    "scale",
    "colorspace",
    "format",
    "zscale",
    "fps",
    "setsar",
    "setdar",
    "crop",
    "pad",
    "eq",
    "unsharp",
    "transpose",
]);

const VF_FLAGS = new Set(["-vf", "-vf:v", "-vf:v:0"]);

function invalid(error: string): FfmpegParamsValidationResult {
    return { valid: false, args: [], error };
}

/**
 * Strips one pair of matching surrounding quotes from a token (e.g. `"a=b"` -> `a=b`).
 * Quoted values never contain spaces — VALUE_PATTERN forbids them — so plain
 * whitespace splitting followed by this is enough. Returns null on an
 * unterminated quote (a quote that isn't part of a surrounding pair).
 */
function stripQuotes(token: string): string | null {
    const first = token[0];
    if (first === '"' || first === "'") {
        if (token.length < 2 || !token.endsWith(first)) {
            return null;
        }
        token = token.slice(1, -1);
    }
    return token.includes('"') || token.includes("'") ? null : token;
}

/** Splits a params line into whitespace-separated args with quotes stripped. Returns null on an unterminated quote. */
function splitParamsLineIntoArgs(line: string): string[] | null {
    const args = line.split(/\s+/).map(stripQuotes);

    return args.includes(null) ? null : (args as string[]);
}

function findDisallowedVideoFilter(value: string): string | undefined {
    return value
        .split(",")
        .map((segment) => segment.split("=")[0].trim())
        .find((filterName) => !ALLOWED_VF_FILTERS.has(filterName));
}

/**
 * Validates the flag at `index` (and its value, if any), then recurses to the
 * next flag — args are a sequence of flags each followed by a value (except
 * valueless flags), so each step advances by 1 or 2. Returns an error message
 * or null. Bounded by MAX_ARGS, so recursion depth is safe.
 */
function findFlagSequenceError(args: string[], index: number): string | null {
    if (index >= args.length) {
        return null;
    }

    const flag = args[index];

    if (!flag.startsWith("-")) {
        return `Unexpected value "${flag}" — expected an ffmpeg flag`;
    }
    if (DENIED_FLAGS.has(flag)) {
        return `Flag "${flag}" is not allowed`;
    }
    if (VALUELESS_FLAGS.has(flag)) {
        return findFlagSequenceError(args, index + 1);
    }
    if (!ALLOWED_FLAG_PATTERN.test(flag)) {
        return `Flag "${flag}" is not allowed`;
    }

    const value = args[index + 1];
    if (value === undefined || value.startsWith("-")) {
        return `Flag "${flag}" requires a value`;
    }
    if (!VALUE_PATTERN.test(value)) {
        return `Value "${value}" contains characters that are not allowed`;
    }
    const lowerValue = value.toLowerCase();
    if (FORBIDDEN_VALUE_SUBSTRINGS.some((substring) => lowerValue.includes(substring))) {
        return `Value "${value}" is not allowed`;
    }
    if (VF_FLAGS.has(flag)) {
        const disallowedFilter = findDisallowedVideoFilter(value);
        if (disallowedFilter !== undefined) {
            return `Video filter "${disallowedFilter}" is not allowed`;
        }
    }

    return findFlagSequenceError(args, index + 2);
}

export function validateCustomFfmpegParams(line: string): FfmpegParamsValidationResult {
    const trimmed = (line || "").trim();

    if (!trimmed) {
        return invalid("Custom ffmpeg params are required");
    }
    if (trimmed.length > MAX_LENGTH) {
        return invalid(`Custom ffmpeg params must be at most ${MAX_LENGTH} characters`);
    }

    const args = splitParamsLineIntoArgs(trimmed);
    if (args === null) {
        return invalid("Unterminated quote in custom ffmpeg params");
    }
    if (args.length > MAX_ARGS) {
        return invalid(`Custom ffmpeg params must be at most ${MAX_ARGS} arguments`);
    }

    const flagError = findFlagSequenceError(args, 0);
    if (flagError) {
        return invalid(flagError);
    }

    return { valid: true, args };
}

export default validateCustomFfmpegParams;
